import React from "react";
import _ from "underscore";
import { Button, Row, Col } from "reactstrap";
import InstanceUtils from "utils/InstanceUtils";
import FilterLimitSection from "./FilterLimitSection";
import FilterOrderBySection from "./FilterOrderBySection";
import FilterCard from "./FilterCard";
import QueryUtils from "utils/QueryUtils";
import FieldUtils from "utils/FieldUtils";
import pluralize from "pluralize";
import Constants from "utils/Constants";
import { callFunctionIfExists } from "utils/GeneralUtils";

const initialSelected = {
  orderBy: false,
  limit: false,
  variables: false,
};

/**
 * fields - Expects the fields that can be used for querying.
 * currentModule - Expects the module that will be used for querying.
 * onChange - Expects a function that handles when the query is updated in this component.
 * existingQuery - Expects existing data for a query or null.
 * showOrderBy - Expects a boolean for whether or not Order By options should appear. Defaulted to false.
 * showLimit - Expects a boolean for whether or not Limit options should appear. Defaulted to false.
 * showVariables - Expects a boolean for whether or not Variables can be entered. Defaulted to false.
 * variables - Expects the variables that the user can place in a query.
 * @returns JSX.Element
 */
class AdvancedFilterMenu extends React.Component {
  state = {
    query: null,
  };

  /**
   * Determines how to load the filters based on an existing query.
   * @param {*} query
   */
  loadFilters(query) {
    if (query) {
      this.loadExistingQuery(query);
    } else {
      this.loadBlankQuery();
    }
  }

  /**
   * Loads an existing query object into the local component's state
   * @param {*} query
   */
  loadExistingQuery(query) {
    let existingAvailableFields = [];
    let variables = [];

    if (!query) {
      return;
    }

    let newQuery = JSON.parse(JSON.stringify(query));

    if (!newQuery?.Filters?.length) {
      newQuery.Filters = QueryUtils.getStartingQuery()?.Query?.Filters;
    }

    for (const group of newQuery.Filters) {
      let variablesGroup = [];
      let groupFields = [];
      let filterGroup = [];

      if (group.Group) {
        groupFields = group.Filters.map((f) => f.Field);
        filterGroup = group.Filters;
      } else {
        groupFields = [group];
        filterGroup = [group];
      }

      let fields = [...this.getStartingFields()].map((f) => {
        let selected = false;
        if (groupFields.includes(f.APIName)) {
          selected = true;
        }

        return { ...f, SelectedForQuery: selected };
      });

      existingAvailableFields.push(fields);

      const variableRegex = /\{\{.*\}\}/g;

      // Set up variables as well
      filterGroup.forEach((filter) => {
        if (filter?.Value && variableRegex.test(filter.Value)) {
          variablesGroup.push(true);
        } else if (
          filter?.Min &&
          (variableRegex.test(filter.Min) || variableRegex.test(filter.Max))
        ) {
          variablesGroup.push(true);
        } else {
          variablesGroup.push(false);
        }
      });
      variables.push(variablesGroup);
    }

    if (this.props.showOrderBy && newQuery.OrderBy?.Field) {
      this.toggleSelected("orderBy");
    }

    if (this.props.showLimit && newQuery.Limit?.Count) {
      this.toggleSelected("limit");
    }

    let queryJSON = JSON.stringify(newQuery?.Filters || {});
    let queryJSONString = JSON.stringify(this.state.query || {});

    if (queryJSON !== queryJSONString) {
      this.handleQueryChange(newQuery);
    }

    this.setState({
      unalterableFields: existingAvailableFields,
      availableFields: existingAvailableFields,
      useVariables: variables,
    });
  }

  /**
   * Loads a blank query into the local component's state
   */
  loadBlankQuery() {
    const blankQuery = QueryUtils.getStartingQuery().Query;

    this.setState({
      query: blankQuery,
      availableFields: [[...this.getStartingFields()]],
      useVariables: [[false]],
      selected: { ...initialSelected },
    });
  }

  /**
   * Handles events where the query has been modified
   * @param {*} query
   */
  handleQueryChange(query) {
    query = JSON.parse(JSON.stringify(query)); // Deep copy query.

    this.setState({ query }, () => {
      callFunctionIfExists(this.props.onChange, query);
    });
  }

  /**
   * Toggles the order by menu visibility.
   */
  toggleOrderBy() {
    let query = this.state.query;
    let selected = this.state.selected;

    if (selected.orderBy) {
      query.OrderBy = {};
      selected.orderBy = false;
    } else {
      query.OrderBy = { Field: "", Direction: "" };
      selected.orderBy = true;
    }

    this.setState({ selected });
    this.handleQueryChange(query);
  }

  /**
   * Toggles the limit visibility
   */
  toggleLimit() {
    let query = this.state.query;
    let selected = this.state.selected;

    if (selected.limit) {
      query.Limit = {};
      selected.limit = false;
    } else {
      selected.limit = true;
    }

    this.setState({
      selected,
    });

    this.handleQueryChange(query);
  }

  /**
   * Determines the starting fields that are available for use.
   */
  getStartingFields() {
    let propFields = this.props.fields
      ? this.props.fields
      : FieldUtils.getFieldsFromModuleOrSubitem(this.props.currentModule);

    if (!propFields) return [];

    propFields = JSON.parse(JSON.stringify(propFields));

    let startingFields = propFields.flatMap((field) => {
      // Filter out unfilterable fields from advanced filter menu
      if (
        field?.Sensitive ||
        Constants.UNFILTERABLE_FIELDS.includes(field.FieldType)
      ) {
        return [];
      }

      return [
        {
          ...field,
          SelectedForQuery: false,
        },
      ];
    });

    startingFields = _.sortBy(startingFields, "Name");

    return startingFields;
  }

  /**
   * Updates the fields available in a filter group.
   *
   * @param {*} groupIndex
   * @param {*} prevField
   * @param {*} newField
   */
  updateAvailableFields(groupIndex, prevField = "", newField = "") {
    const availableFieldsCopy = [...this.state.availableFields];

    const prevFieldIndex = _.findIndex(this.state.availableFields[groupIndex], {
      APIName: prevField,
    });
    const newFieldIndex = _.findIndex(this.state.availableFields[groupIndex], {
      APIName: newField,
    });

    if (prevFieldIndex !== -1) {
      availableFieldsCopy[groupIndex][prevFieldIndex].SelectedForQuery = false;
    }
    if (newFieldIndex !== -1) {
      availableFieldsCopy[groupIndex][newFieldIndex].SelectedForQuery = true;
    }

    this.setState({
      availableFields: availableFieldsCopy,
    });
  }

  /**
   * Handler for when the and condition button is toggled to add/remove a condition from the filters.
   *
   * @param {*} groupIndex
   * @param {*} filterIndex
   * @param {*} isLastFilter
   */
  handleAndClick(groupIndex, filterIndex, isLastFilter) {
    const filtersCopy = [...(this.state.query?.Filters || [])];
    const useVariablesCopy = [...this.state.useVariables];
    const selected = filtersCopy[groupIndex]?.Filters[filterIndex]?.Field;

    if (isLastFilter) {
      filtersCopy[groupIndex].Filters.push(QueryUtils.getEmptyFilter());
      useVariablesCopy[groupIndex].push(false);
    } else {
      filtersCopy[groupIndex].Filters.splice(filterIndex, 1);
      useVariablesCopy[groupIndex].splice(filterIndex, 1);
      if (selected && selected !== "") {
        this.updateAvailableFields(groupIndex, selected);
      }
    }

    this.setState({
      useVariables: useVariablesCopy,
    });

    this.handleQueryChange({ ...this.state.query, Filters: filtersCopy });
  }

  /**
   * Event handler for when a filter group is added/removed.
   *
   * @param {*} groupIndex
   * @param {*} isLastCard
   */
  handleOrClick(groupIndex, isLastCard) {
    const filtersCopy = [...(this.state.query?.Filters || [])];
    const useVariablesCopy = [...this.state.useVariables];
    const availableFieldsCopy = [...this.state.availableFields];

    if (isLastCard) {
      filtersCopy.push(QueryUtils.getEmptyFilterGroup());
      useVariablesCopy.push([false]);
      availableFieldsCopy.push(this.getStartingFields());
    } else {
      filtersCopy.splice(groupIndex + 1, 1);
      useVariablesCopy.splice(groupIndex + 1, 1);
      availableFieldsCopy.splice(groupIndex + 1, 1);
    }

    this.setState({
      useVariables: useVariablesCopy,
      availableFields: availableFieldsCopy,
    });

    this.handleQueryChange({ ...this.state.query, Filters: filtersCopy });
  }

  /**
   * Handler for when variables are toggled as visible/not visible
   *
   * @param {*} groupIndex
   * @param {*} filterIndex
   */
  handleVariableToggle(groupIndex, filterIndex) {
    const useVariablesCopy = [...this.state.useVariables];

    useVariablesCopy[groupIndex][filterIndex] =
      !useVariablesCopy[groupIndex][filterIndex];

    this.setState({
      useVariables: useVariablesCopy,
    });
  }

  /**
   * Handles event when a specific filter input gets modified.
   *
   * @param {*} field
   * @param {*} value
   * @param {*} designator
   * @param {*} groupIndex
   * @param {*} filterIndex
   */
  handleFilterInputChange(field, value, designator, groupIndex, filterIndex) {
    const filtersCopy = [...(this.state.query?.Filters || [])];

    let val = InstanceUtils.formatInputComponentResponse(field, value);

    const currentFilter = filtersCopy[groupIndex].Filters[filterIndex];
    if (designator === "Value") {
      currentFilter.Value = val;
      delete currentFilter.Min;
      delete currentFilter.Max;
    } else {
      currentFilter[designator] = val;
      delete currentFilter.Value;
    }

    //setQuery((prev) => ({ ...prev, Filters: filtersCopy }));
    this.handleQueryChange({ ...this.state.query, Filters: filtersCopy });
  }

  /**
   * Handles events when a select filter is changed.
   *
   * @param {*} item: { value: string, source?: string }
   * @param {*} groupIndex
   * @param {*} filterIndex
   * @param {*} selectType
   */
  handleSelectChange(item, groupIndex, filterIndex, selectType) {
    const newValue = item.value;
    const source = item?.source;
    const filtersCopy = [...(this.state.query?.Filters || [])];
    const currentFilter = filtersCopy[groupIndex].Filters[filterIndex];

    if (selectType === "Field") {
      this.updateAvailableFields(groupIndex, currentFilter.Field, newValue);

      currentFilter.Method = "";
      delete currentFilter.Min;
      delete currentFilter.Max;
      delete currentFilter.Value;
    }

    currentFilter[selectType] = newValue;

    if (source) {
      currentFilter.Source = source;
      currentFilter.RecordSummary = true;
      currentFilter.RecordSummarySource = source;
    }

    //setQuery((prev) => ({ ...prev, Filters: filtersCopy }));
    this.handleQueryChange({ ...this.state.query, Filters: filtersCopy });
  }

  /**
   * Handler for when the order by conditions are changed
   * @param {*} name
   * @param {*} value
   */
  handleOrderByChange(name, value) {
    const queryCopy = { ...this.state.query };

    queryCopy.OrderBy = { ...queryCopy.OrderBy, [name]: value };

    this.handleQueryChange(queryCopy);
  }

  /**
   * Handler for when the limit is changed.
   * @param {event} e
   */
  handleLimitChange(e) {
    const queryCopy = { ...this.state.query };
    const newLimit = e.target.value;

    queryCopy.Limit = { Count: newLimit };

    this.handleQueryChange(queryCopy);
  }

  componentDidMount() {
    this.loadFilters(this.props?.existingQuery);
  }

  componentDidUpdate(prevProps) {
    let jsonCurrent = JSON.stringify(this.props.existingQuery || {});
    let jsonPrevious = JSON.stringify(prevProps.existingQuery || {});

    if (jsonPrevious !== jsonCurrent) {
      this.loadFilters(this.props.existingQuery);
    }
  }

  render() {
    return (
      <div>
        <div className="mb-2">
          <h4 className="m-0">
            Every{" "}
            {pluralize.singular(this.props?.currentModule?.Name || "record")}{" "}
            matching...
          </h4>
        </div>

        {this.state.query?.Filters?.length > 0 &&
          this.state.query.Filters.map((card, index) => {
            const isLastCard = index === this.state.query?.Filters.length - 1;
            return (
              <FilterCard
                unalterableFields={FieldUtils.getFieldsFromModuleOrSubitem(
                  this.props.currentModule
                )}
                key={index}
                currentModule={this.props?.currentModule}
                filters={this.state.query?.Filters}
                filter={card}
                groupIndex={index}
                isLastCard={isLastCard}
                availableFields={this.state.availableFields}
                showVariables={this.props.showVariables}
                useVariables={this.state.useVariables}
                handleVariableToggle={this.handleVariableToggle.bind(this)}
                handleSelectChange={this.handleSelectChange.bind(this)}
                handleFilterInputChange={this.handleFilterInputChange.bind(
                  this
                )}
                handleAndClick={this.handleAndClick.bind(this)}
                handleOrClick={this.handleOrClick.bind(this)}
                subitemFieldsOnly={this.props.subitemFieldsOnly || false}
              />
            );
          })}
        {(this.props.showOrderBy || this.props.showLimit) && (
          <hr className="my-3"></hr>
        )}
        <Row className="mb-4">
          <Col xs="12">
            {this.props.showOrderBy && (
              <Button
                size="sm"
                className="ml-0 text-uppercase"
                outline={!this.state.selected.orderBy}
                onClick={this.toggleOrderBy.bind(this)}
              >
                Order By
              </Button>
            )}
            {this.props.showLimit && (
              <Button
                size="sm"
                className="ml-2 text-uppercase"
                outline={!this.state.selected.limit}
                onClick={() => this.toggleLimit()}
              >
                Limit To
              </Button>
            )}
          </Col>
        </Row>

        {this.props.showOrderBy && this.state.selected.orderBy && (
          <FilterOrderBySection
            field={this.state.query.OrderBy.Field}
            direction={this.state.query.OrderBy.Direction}
            handleOrderByChange={this.handleOrderByChange.bind(this)}
            staticFields={this.state.staticFields}
          />
        )}

        {this.props.showLimit && this.state.selected.limit && (
          <FilterLimitSection
            count={this.state.query?.Limit?.Count}
            handleLimitChange={this.handleLimitChange.bind(this)}
          />
        )}
      </div>
    );
  }
}

export default AdvancedFilterMenu;
